import type {
	Collection,
	CollectionDefaults,
	FindOptions,
	ResolvedCollection,
	StoreSchema,
} from '@rstore/shared';
import type { useAppConfig } from '#app/config';
import type { Store } from '#imports';
import type { CrudActionType as CrudActionName } from '#layers/access/public';
import type { FieldSelection, Filter, PrimitivePrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { isNil, stringifyQuery } from '#layers/base/public';
import { isNonEmptyFilter, isRelationalFilter, mergeAnd, toApplicableFilter } from '#layers/data-model/public';
import { apiEndpoint } from './api-endpoint';
import { flattenFieldSelection } from './flatten-field-selection';
import { getFields } from './get-fields';
import {
	hasDeepFilterEntry,
	normalizeDeepFilterOperandsForBackend,
	normalizeFilterOperandsForBackend,
} from './normalize-filter-operands-for-backend';

interface GetFetchOptions {
	appConfig: ReturnType<typeof useAppConfig>;
	store: Store;
	collection: ResolvedCollection;
	findOptions?: FindOptions<Collection, CollectionDefaults, StoreSchema>;
	key?: PrimitivePrimaryKey;
	fields?: FieldSelection;
	filter?: Filter;
	operation: CrudActionName;
}

/**
 * Resolves the workspace slug that should prefix the rstore API endpoint.
 */
function resolveWorkspaceSlug(options: Pick<GetFetchOptions, 'appConfig' | 'collection' | 'findOptions'>): string | undefined {
	const workspaceSlug = options.findOptions?.workspaceSlug
		?? (options.collection.meta?.useWorkspaceSlug ? options.appConfig.monospace.workspaceSlug : undefined);

	if (workspaceSlug === '' || (options.collection.meta?.useWorkspaceSlug && !workspaceSlug)) {
		throw new Error(`No workspace slug available for rstore collection "${options.collection.name}"`);
	}

	return workspaceSlug;
}

/**
 * Combines endpoint, fields, filters, pagination, and custom params into the API request shape.
 *
 * Structured filters are normalized before query-string serialization so canonical in-memory
 * operands, such as Temporal date/time values, become backend wire values at one boundary.
 */
export function getFetchOptions(options: GetFetchOptions) {
	const { appConfig, store, collection, findOptions, key, filter, operation } = options;
	const workspaceSlug = resolveWorkspaceSlug({ appConfig, collection, findOptions });

	const endpoint = apiEndpoint({
		collection,
		key,
		operation,
		workspaceSlug,
	});
	const rawFields = options.fields ?? getFields({ store, collection, findOptions });
	const { fields: resolvedFields, deep } = flattenFieldSelection(rawFields);

	let finalFilter: Filter | undefined;

	if (isNonEmptyFilter(filter) && isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = mergeAnd(filter, findOptions.filter);
	}
	else if (isNonEmptyFilter(filter)) {
		finalFilter = filter;
	}
	else if (isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = findOptions.filter;
	}

	// Normalize open ranges and remove incomplete clauses before request serialization.
	finalFilter = finalFilter ? toApplicableFilter(finalFilter) : finalFilter;

	const hasDeepFilter = deep ? hasDeepFilterEntry(deep) : false;
	const needsSchemaCollection = hasDeepFilter || isRelationalFilter(finalFilter);
	const schemaCollection = needsSchemaCollection
		? store.MonospaceCollection?.peekFirst({ filter: { apiName: collection.name } }) ?? undefined
		: undefined;
	const primitiveFieldTypes = collection.meta?.primitiveFieldTypes;
	const normalizationContext = {
		primitiveFieldTypes,
		schemaCollection,
		getPrimitiveFieldTypes: (schemaCollection: ResolvedMonospaceCollection) => {
			const collection = store.$collections.find((collection) => collection.name === schemaCollection.apiName);

			return collection?.meta?.primitiveFieldTypes;
		},
	};

	const normalizedFilter = finalFilter
		? normalizeFilterOperandsForBackend(finalFilter, normalizationContext)
		: finalFilter;
	const normalizedDeep = deep && hasDeepFilter && normalizationContext.schemaCollection
		? normalizeDeepFilterOperandsForBackend(deep, normalizationContext)
		: deep;

	const { pageSize, pageIndex } = findOptions ?? {};
	const hasPage = !isNil(pageIndex);
	const hasPageSize = !isNil(pageSize);
	const paginated = hasPage && hasPageSize;

	if (hasPage && !hasPageSize) {
		console.error('findOptions has pageIndex but no pageSize', findOptions);
	}

	const query = stringifyQuery({
		fields: resolvedFields.length > 0 ? resolvedFields.join(',') : undefined,
		filter: normalizedFilter,
		sort: findOptions?.sort,
		deep: normalizedDeep,
		...findOptions?.params,
		...paginated
			? {
				// Simple pagination = previous/next
				// Load an extra item to check for next page
				// TODO handle full pagination (shouldn't need to add an extra item)
					meta: { totalCount: true },
					limit: pageSize + 1,
					offset: pageIndex * pageSize,
				}
			: {},
	});

	// Sanity checks
	if (findOptions?.fetchPolicy !== 'no-cache' && isRelationalFilter(filter)) {
		console.warn(`The following query contains a relational filter that can not be safely evaluated on the client: ${endpoint}?${query}`, filter);
	}

	return {
		endpoint,
		query,
		fields: resolvedFields,
		paginated,
	};
}
